import React, { useMemo, useState } from 'react';
import { Box } from '@amzn/awsui-components-console';
import { AttributeRow, AutoPopulationDefinition } from './configureAttributeStep.types';

interface AttributeTableProps {
	items: AttributeRow[];
	definitions: Record<string, AutoPopulationDefinition>;
	filterText: string;
	selectedAttribute: AttributeRow | null;
	onSelectAttribute: (row: AttributeRow) => void;
	customOrder: string[] | null;
	onReorder: (order: string[]) => void;
}

const filterRows = (attrs: AttributeRow[], filterText: string): AttributeRow[] => {
	if (!filterText) return attrs;
	const q = filterText.toLowerCase();

	return attrs.filter(
		(a) => a.attributeName.toLowerCase().includes(q) || a.attributeId.toLowerCase().includes(q)
	);
};

const rankSort = (
	attrs: AttributeRow[],
	definitions: Record<string, AutoPopulationDefinition>
): AttributeRow[] =>
	[...attrs].sort((a, b) => {
		const rank = (row: AttributeRow) => {
			const def = definitions[row.attributeId];

			if (def?.dependents?.length) return 0;
			if (def?.autoPopulationId && def.autoPopulationId !== row.attributeId) return 1;
			if (row.autoPopulationId !== 'None') return 1;

			return 2;
		};

		return rank(a) - rank(b);
	});

const applyCustomOrder = (attrs: AttributeRow[], order: string[]): AttributeRow[] => {
	const byId = new Map(attrs.map((a) => [a.attributeId, a]));
	const ordered: AttributeRow[] = [];

	order.forEach((id) => {
		const row = byId.get(id);

		if (row) {
			ordered.push(row);
			byId.delete(id);
		}
	});
	byId.forEach((row) => ordered.push(row));

	return ordered;
};

export default function AttributeTable({
	items,
	definitions,
	filterText,
	selectedAttribute,
	onSelectAttribute,
	customOrder,
	onReorder,
}: AttributeTableProps) {
	const [draggingId, setDraggingId] = useState<string | null>(null);
	const [dropTargetId, setDropTargetId] = useState<string | null>(null);

	const orderedItems = useMemo(() => {
		const filtered = filterRows(items, filterText);

		return customOrder
			? applyCustomOrder(filtered, customOrder)
			: rankSort(filtered, definitions);
	}, [items, filterText, definitions, customOrder]);

	const reorder = (sourceId: string, targetId: string) => {
		if (sourceId === targetId) return;
		const allIds = (customOrder ?? items.map((a) => a.attributeId)).filter((id) =>
			items.some((a) => a.attributeId === id)
		);
		const missing = items
			.map((a) => a.attributeId)
			.filter((id) => !allIds.includes(id));
		const next = [...allIds, ...missing];
		const fromIdx = next.indexOf(sourceId);
		const toIdx = next.indexOf(targetId);

		if (fromIdx === -1 || toIdx === -1) return;
		next.splice(fromIdx, 1);
		next.splice(toIdx, 0, sourceId);
		onReorder(next);
	};

	if (orderedItems.length === 0) {
		return (
			<div data-testid="attribute-table">
				<Box textAlign="center" padding="m">
					No attributes
				</Box>
			</div>
		);
	}

	return (
		<div role="table" aria-label="Attributes" data-testid="attribute-table">
			<div
				role="row"
				style={{
					display: 'grid',
					gridTemplateColumns: '32px 2fr 2fr 1fr',
					padding: '8px 12px',
					borderBottom: '1px solid #e9ebed',
					fontWeight: 700,
				}}
			>
				<div role="columnheader" />
				<div role="columnheader">Attribute Name</div>
				<div role="columnheader">AutoPopulation Operation</div>
				<div role="columnheader">Validation Operation</div>
			</div>
			{orderedItems.map((row) => {
				const isSelected = selectedAttribute?.attributeId === row.attributeId;
				const isDragging = draggingId === row.attributeId;
				const isDropTarget = dropTargetId === row.attributeId && draggingId !== row.attributeId;
				const background = isSelected ? '#eaf2ff' : 'transparent';
				const opacity = isDragging ? 0.4 : 1;
				const borderTop = isDropTarget ? '2px solid #0972d3' : '1px solid transparent';

				return (
					<div
						key={row.attributeId}
						role="row"
						data-testid="attribute-row"
						draggable
						aria-selected={isSelected}
						onDragStart={(e) => {
							setDraggingId(row.attributeId);
							e.dataTransfer.effectAllowed = 'move';
							e.dataTransfer.setData('text/plain', row.attributeId);
						}}
						onDragEnter={() => setDropTargetId(row.attributeId)}
						onDragOver={(e) => {
							e.preventDefault();
							e.dataTransfer.dropEffect = 'move';
						}}
						onDragEnd={() => {
							setDraggingId(null);
							setDropTargetId(null);
						}}
						onDrop={(e) => {
							e.preventDefault();
							const sourceId = e.dataTransfer.getData('text/plain') || draggingId;

							if (sourceId) reorder(sourceId, row.attributeId);
							setDraggingId(null);
							setDropTargetId(null);
						}}
						onClick={() => onSelectAttribute(row)}
						style={{
							display: 'grid',
							gridTemplateColumns: '32px 2fr 2fr 1fr',
							alignItems: 'center',
							padding: '8px 12px',
							background,
							opacity,
							borderTop,
							borderBottom: '1px solid #e9ebed',
							cursor: 'pointer',
							userSelect: 'none',
						}}
					>
						<div
							aria-label="Drag to reorder"
							title="Drag to reorder"
							style={{
								cursor: 'grab',
								color: '#5f6b7a',
								textAlign: 'center',
								fontSize: '14px',
								lineHeight: 1,
							}}
							onClick={(e) => e.stopPropagation()}
						>
							⋮⋮
						</div>
						<div>{row.attributeName}</div>
						<div>{row.autoPopulationId}</div>
						<div>{row.validationId}</div>
					</div>
				);
			})}
		</div>
	);
}